import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { UNMARK_ROUTE_PROVIDER } from 'src/graphql/requests/unmarkRouteProvider';
import { GUnmarkRouteProviderMutation } from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import { firstUserErrorMessage } from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';
import { getRoutes } from './getRoutes';

type UnmarkRouteProviderArgs = {
  routeId: string;
  warehouseId: string;
  nightEnd: string;
};

export const unmarkRouteProvider = createAsyncThunk(
  'delivery/unmarkRouteProvider',
  async (
    { routeId, warehouseId, nightEnd }: UnmarkRouteProviderArgs,
    thunkAPI,
  ): Promise<boolean> => {
    const { data, error } = await client
      .mutation<GUnmarkRouteProviderMutation>(UNMARK_ROUTE_PROVIDER, {
        input: { routeId },
      })
      .toPromise();

    const payload = data?.unmarkRouteProvider;
    const userErrors = payload?.userErrors ?? [];
    const ok = !error && Boolean(payload) && userErrors.length === 0;

    if (!ok) {
      logWarning('third_party.unmark_route_failed', {
        routeId,
        userError: userErrors[0]?.__typename,
        transportError: Boolean(error),
      });
    }
    thunkAPI.dispatch(
      showNotification(
        ok
          ? { type: NotificationType.Success, message: 'Route removed.' }
          : {
              type: NotificationType.Error,
              // "push in progress, retry shortly" is the usual one: only the
              // server knows the push is running.
              message: userErrors.length
                ? firstUserErrorMessage(userErrors)
                : undefined,
            },
      ),
    );
    // On every outcome: a refusal means the header showed a state LDS has
    // moved past, and a transport error may hide a committed unmark.
    thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getAllStopStatuses({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getPrimaryStopStatuses({ warehouseId, nightEnd }));
    return ok;
  },
);
